import { Box, Button, Text, useToast } from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { EditorState, convertToRaw, ContentState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import Loading from "../components/common/ui/Loading";
import { useNavigator } from "../hooks/useNavigator";
import { getDashboardData, systemValueUpdate, uploadFile } from "../utils/api";

const AdRules = () => {
  const [systemValues, setSystemValues] = useState({});
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const toast = useToast();

  const getDashboardDataHandler = async () => {
    setLoading(true);
    const [success, data] = await getDashboardData();
    if (success) {
      setSystemValues(data.systemValues || {});
      const htmlContent = data.systemValues?.adsRules || "";
      if (htmlContent) {
        const contentBlock = htmlToDraft(htmlContent);
        if (contentBlock) {
          const contentState = ContentState.createFromBlockArray(
            contentBlock.contentBlocks
          );
          const editorState = EditorState.createWithContent(contentState);
          setEditorState(editorState);
        }
      } else {
        setEditorState(EditorState.createEmpty());
      }
    }
    setLoading(false);
  };

  const [navigate, user] = useNavigator(() => {
    if (user) {
      getDashboardDataHandler();
    } else {
      navigate("/login");
    }
  });

  const handleSave = async () => {
    setSaving(true);
    const htmlContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    const updatedSystemValues = {
      ...systemValues,
      adsRules: htmlContent,
    };
    const [success, data] = await systemValueUpdate(updatedSystemValues);
    if (success) {
      toast({
        title: "Event Rules Updated",
        description: "Event Rules Updated Successfully",
        status: "success",
        duration: 9000,
        isClosable: true,
      });
      getDashboardDataHandler();
    } else {
      toast({
        title: "Error",
        description: "Failed to update Event Rules",
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }
    setSaving(false);
  };

  const handleReset = () => {
    const htmlContent = systemValues?.adsRules || "";
    if (htmlContent) {
      const contentBlock = htmlToDraft(htmlContent);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(
          contentBlock.contentBlocks
        );
        const editorState = EditorState.createWithContent(contentState);
        setEditorState(editorState);
      }
    } else {
      setEditorState(EditorState.createEmpty());
    }
  };

  const handleImageUpload = async (file) => {
    return new Promise((resolve, reject) => {
      uploadFile(file)
        .then(([success, imageUrl]) => {
          if (success) {
            toast({
              title: "Image Uploaded",
              description: "Image uploaded successfully",
              status: "success",
              duration: 3000,
              isClosable: true,
            });
            resolve({ data: { link: imageUrl } });
          } else {
            toast({
              title: "Upload Failed",
              description: "Failed to upload image",
              status: "error",
              duration: 3000,
              isClosable: true,
            });
            reject(new Error("Failed to upload image"));
          }
        })
        .catch((error) => {
          toast({
            title: "Upload Error",
            description: "An error occurred while uploading image",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          reject(error);
        });
    });
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box sx={{ p: "10" }}>
      <Text
        fontSize="4xl"
        fontWeight="bold"
        mb="5"
        sx={{
          padding: "1rem",
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: "1px solid #eee",
        }}
      >
        Event Rules Editor
      </Text>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          padding: "1rem",
        }}
      >
        <Box>
          <Text fontSize="lg" fontWeight="semibold" mb="2">
            HTML Content Editor
          </Text>
          <Box
            sx={{
              border: "1px solid #e2e8f0",
              borderRadius: "0.5rem",
              backgroundColor: "#ffffff",
              "& .rdw-editor-wrapper": {
                minHeight: "400px",
              },
              "& .rdw-editor-main": {
                minHeight: "400px",
                padding: "1rem",
              },
            }}
          >
            <Editor
              editorState={editorState}
              onEditorStateChange={setEditorState}
              toolbar={{
                options: [
                  "inline",
                  "blockType",
                  "fontSize",
                  "fontFamily",
                  "list",
                  "textAlign",
                  "colorPicker",
                  "link",
                  "embedded",
                  "emoji",
                  "image",
                  "remove",
                  "history",
                ],
                inline: {
                  inDropdown: false,
                  className: undefined,
                  component: undefined,
                  dropdownClassName: undefined,
                  options: ["bold", "italic", "underline", "strikethrough"],
                },
                blockType: {
                  inDropdown: true,
                  options: [
                    "Normal",
                    "H1",
                    "H2",
                    "H3",
                    "H4",
                    "H5",
                    "H6",
                    "Blockquote",
                  ],
                },
                fontSize: {
                  options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                },
                list: {
                  inDropdown: false,
                  options: ["unordered", "ordered", "indent", "outdent"],
                },
                textAlign: {
                  inDropdown: false,
                  options: ["left", "center", "right", "justify"],
                },
                link: {
                  inDropdown: false,
                  showOpenOptionOnHover: true,
                  defaultTargetOption: "_self",
                  options: ["link", "unlink"],
                },
                image: {
                  uploadEnabled: true,
                  uploadCallback: handleImageUpload,
                  previewImage: true,
                  inputAccept: "image/gif,image/jpeg,image/jpg,image/png,image/svg",
                  alt: { present: false, mandatory: false },
                  defaultSize: {
                    height: "auto",
                    width: "auto",
                  },
                },
              }}
              placeholder="Enter HTML content for Event Rules..."
            />
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            justifyContent: "flex-end",
          }}
        >
          <Button
            colorScheme="gray"
            onClick={handleReset}
            isDisabled={saving}
          >
            Reset
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSave}
            isLoading={saving}
            loadingText="Saving..."
          >
            Save Changes
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdRules;
